import { AxiosResponse } from 'axios';
import { CreditConsumptionEstablishment } from '@establishment/models/get-credit-consumption';
import EndPoints from '@services/endpoints';
import { EstablishmentCreditTopUp } from '@establishment/models/get-credit-topup';
import EstablishmentItem from '@establishment/models/dto/establishment-item.response.dto';
import EstablishmentMutationPayload from '@establishment/models/dto/establishment-mutation.dto';
import IPaginatedParams from '@models/paginated-params';
import IPaginatedesponse from '@models/paginated-reponse';
import ISearchCriteria from '@models/dto/searchCriteria.dto';
import axiosClient from '@services/axiosClient';
import getEstablishmentResponseDto from '@establishment/models/dto/get-establishment.response.dto';

export const getEstablishments = async (
  paginatedParams: IPaginatedParams,
  searchCriteria: ISearchCriteria,
): Promise<AxiosResponse<IPaginatedesponse<EstablishmentItem>>> => {
  return await axiosClient.post(
    EndPoints.establishment.getEstablishment(paginatedParams),
    searchCriteria,
  );
};

export const getEstablishmentSuggestions = async (
  params: IPaginatedParams,
): Promise<AxiosResponse<IPaginatedesponse<{ id: string; name: string }>>> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentsSuggestions(params),
  );
};

export const getEstablishmentSuggestionsByEnterpriseId = async (
  params: IPaginatedParams,
  enterpriseId: string,
): Promise<AxiosResponse<{ id: string; name: string }[]>> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentsSuggestionsByEnterpriseId(
      params,
      enterpriseId,
    ),
  );
};

export const disableEstablishmentById = async (
  id: string,
  sendEmail: boolean,
): Promise<AxiosResponse<null>> => {
  return await axiosClient.put(
    EndPoints.establishment.disableEstablishmentById(id),
    { sendEmail },
  );
};

export const enableEstablishmentById = async (
  id: string,
): Promise<AxiosResponse<null>> => {
  return await axiosClient.put(
    EndPoints.establishment.enableEstablishmentById(id),
  );
};

export const createEstablishment = async (
  formData: FormData,
): Promise<AxiosResponse<null>> => {
  return await axiosClient.post(
    EndPoints.establishment.createEstablishment(),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const editEstablishment = async (
  id: string,
  formData: FormData | EstablishmentMutationPayload,
): Promise<AxiosResponse<null>> => {
  return await axiosClient.patch(
    EndPoints.establishment.editEstablishment(id),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const getEstablishmentById = async (
  id: string | undefined,
): Promise<AxiosResponse<getEstablishmentResponseDto>> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentById(id),
  );
};

export const getEstablishmentByEnterpriseIdWithThreshold = async (
  enterpriseId: string,
): Promise<
  AxiosResponse<{ id: string; name: string; creditsAlertThreshold: number }[]>
> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentByEnterpriseIdWithThreshold(
      enterpriseId,
    ),
  );
};

export const getEstablishmentCreditsConsumption = async (
  id: string | undefined,
  params: IPaginatedParams,
): Promise<
  AxiosResponse<IPaginatedesponse<CreditConsumptionEstablishment>>
> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentCreditsConsumption(id, params),
  );
};

export const getEstablishmentCreditsTopup = async (
  id: string | undefined,
  params: IPaginatedParams,
): Promise<AxiosResponse<IPaginatedesponse<EstablishmentCreditTopUp>>> => {
  return await axiosClient.get(
    EndPoints.establishment.getEstablishmentCreditsTopup(id, params),
  );
};
